import { MethodResult, Problem, Step } from './types';
import { cloneAlloc, makeMatrix, totalCost } from './utils';

// Método del Costo Mínimo.
// En cada paso se elige la celda disponible con el menor costo unitario
// y se le asigna el máximo posible.
export function minimumCost(p: Problem): MethodResult {
  const m = p.supply.length;
  const n = p.demand.length;
  const alloc = makeMatrix<number | null>(m, n, null);
  const supply = p.supply.slice();
  const demand = p.demand.slice();
  const rowDone = new Array(m).fill(false);
  const colDone = new Array(n).fill(false);
  const steps: Step[] = [];

  steps.push({
    title: 'Inicio',
    description:
      'En cada iteración buscamos la celda con menor costo unitario (entre filas y columnas con oferta/demanda restante) y le asignamos el máximo posible.',
    alloc: cloneAlloc(alloc),
    remainingSupply: supply.slice(),
    remainingDemand: demand.slice(),
  });

  let count = 1;

  while (true) {
    // Buscar la celda de menor costo entre las disponibles.
    let best: { i: number; j: number; c: number } | null = null;
    for (let i = 0; i < m; i++) {
      if (rowDone[i]) continue;
      for (let j = 0; j < n; j++) {
        if (colDone[j]) continue;
        const c = p.costs[i][j];
        if (best === null || c < best.c) best = { i, j, c };
      }
    }
    if (best === null) break;

    const { i, j } = best;
    const amount = Math.min(supply[i], demand[j]);
    alloc[i][j] = amount;
    supply[i] -= amount;
    demand[j] -= amount;

    let reason: string;
    if (supply[i] === 0 && demand[j] === 0) {
      reason = `Se agotan oferta y demanda; cerramos fila ${p.sources[i]} y columna ${p.dests[j]}.`;
      rowDone[i] = true;
      colDone[j] = true;
    } else if (supply[i] === 0) {
      reason = `Se agota la oferta de ${p.sources[i]}; cerramos su fila.`;
      rowDone[i] = true;
    } else {
      reason = `Se satisface la demanda de ${p.dests[j]}; cerramos su columna.`;
      colDone[j] = true;
    }

    steps.push({
      title: `Paso ${count}`,
      description: `Menor costo disponible = ${best.c} en (${p.sources[i]}, ${p.dests[j]}). Asignamos mín(oferta=${
        supply[i] + amount
      }, demanda=${demand[j] + amount}) = ${amount}. ${reason}`,
      alloc: cloneAlloc(alloc),
      remainingSupply: supply.slice(),
      remainingDemand: demand.slice(),
      highlight: { row: i, col: j },
    });
    count++;
  }

  const cost = totalCost(alloc, p.costs);
  steps.push({
    title: 'Solución inicial',
    description: `Se completaron todas las asignaciones. Costo total de la solución inicial = ${cost}.`,
    alloc: cloneAlloc(alloc),
    remainingSupply: supply.slice(),
    remainingDemand: demand.slice(),
  });

  return { alloc, steps, totalCost: cost };
}
